import { CountryCode } from '@constants'
import { type CartCalculateResult } from '@hooks-api/types/dataModel/coupon'
import { floorToDecimal } from '@utils/floorNumber'
import { isAfter, isBefore } from 'date-fns'
import { type RewardPointsBaseStore } from '../types'

export const setUserInputRewardPoints = (state: RewardPointsBaseStore, rewardPoints: number): RewardPointsBaseStore => {
  return {
    ...state,
    userInputRewardPoints: rewardPoints,
  }
}

export const setIsUserAppliedRewardPoints = (state: RewardPointsBaseStore, isUserAppliedRewardPoints: boolean): RewardPointsBaseStore => {
  return {
    ...state,
    isUserAppliedRewardPoints,
  }
}

export const floorToDecimalByCountry = (num: number, countryCode: CountryCode) => {
  const isUS = countryCode === CountryCode.US
  return floorToDecimal(num, isUS ? 1 : 0)
}

/**
 * 取得點數可折抵金額
 * @param rewardPoints 點數
 * @param expenseRate 多少點可以折抵一元
 * @returns 點數可折抵金額
 */
export const getRewardPointsDiscountAmount = (
  rewardPoints: number,
  expenseRate: number,
  countryCode: CountryCode,
) => {
  return floorToDecimalByCountry(rewardPoints / expenseRate, countryCode)
}

/**
 * 計算最高可折抵金額
 * Spec: 上限 = (商品總額 − 優惠券折抵總額) × 折抵比率
 * @param currentRewardPoints 目前可用點數
 * @param expenseRate 折抵率（多少點折抵 1 元）
 * @param subtotal 商品總額（未扣優惠券）
 * @param pointDiscountLimitRate 整單最高可折抵金額比率
 * @param countryCode 國家代碼（用於決定小數位數）
 * @param totalCouponDiscount 優惠券 + 推薦碼已折抵金額（用於計算點數折抵的基準）
 */
export const getMaxRewardPointsDiscountAmount = (
  currentRewardPoints: number,
  expenseRate: number,
  subtotal: CartCalculateResult['subtotal'],
  pointDiscountLimitRate: number,
  countryCode: CountryCode,
  totalCouponDiscount: number,
) => {
  // 目前可用點數最高可折抵金額
  const currentRewardPointsDiscountAmount = getRewardPointsDiscountAmount(
    currentRewardPoints,
    expenseRate,
    countryCode,
  )

  // 整單最高可折抵金額（優惠券 + 推薦碼折抵後的金額為基準）
  const orderMaxAmount = floorToDecimalByCountry(
    Math.max(0, subtotal - totalCouponDiscount) * pointDiscountLimitRate,
    countryCode,
  )

  return Math.min(currentRewardPointsDiscountAmount, orderMaxAmount)
}

/**
 * 判斷是否在活動時間內
 * TODO: 不處理時區問題，後台設定的時間需要轉換為 UTC 時間才可以
 * @param activityStartTime 活動開始時間
 * @param activityEndTime 活動結束時間
 * @returns 是否在活動時間內
 */
export const isActivityTime = (activityStartTime: string | undefined, activityEndTime: string | undefined) => {
  if (!activityStartTime || !activityEndTime) {
    return false
  }
  const now = new Date()
  const startTime = new Date(activityStartTime)
  const endTime = new Date(activityEndTime)
  return isAfter(now, startTime) && isBefore(now, endTime)
}

/**
 * 計算本次消費可獲得的點數
 * Spec: 可獲得的點數是看折扣後的總金額，而不是運費後的總金額
 * @param hasActivity 是否啟用活動
 * @param incomeRate 當前倍率
 * @param activityRate 當前活動倍率
 * @param totalAfterDiscount 折扣後的總金額
 * @returns 本次消費可獲得的點數
 */
export const getEarnedRewardPointAmount = (
  hasActivity: boolean,
  incomeRate: number,
  activityRate: number,
  totalAfterDiscount: CartCalculateResult['total_after_discount'],
) => {
  /**
   * 活動啟用時，使用活動倍率，否則不變 (活動倍率 = 1)
   */
  const DEFAULT_RATE = 1
  const currentActivityRate = hasActivity ? activityRate : DEFAULT_RATE
  /**
   * 計算本次消費可獲得的點數
   * 1. 先計算出可獲得點數
   * 2. 再乘以活動倍率
   */
  return Math.round(totalAfterDiscount / incomeRate * currentActivityRate) || 0 // or 0 防止除以 0 的錯誤
}

export interface RewardPointsCalculateResultType {
  income_rate: number
  activity_rate: number
  expense_rate: number
  point_discount_limit_rate: number
  current_reward_points: number
  activity_start_time?: string
  activity_end_time?: string
  is_activity: boolean
  /** 商品總額（未扣優惠券），用於計算點數折抵上限 */
  subtotal: number
  /** 折扣後總金額（含優惠券），用於計算本次消費可獲得的點數 */
  total_after_discount: number
  country_code: CountryCode
  point_unit_step: number
  currency_unit_step: number
  total_coupon_discount: number
}

export interface RewardPointsCalculateResultReturnType {
  incomeRate: number
  activityRate: number
  expenseRate: number
  pointDiscountLimitRate: number
  totalAfterDiscount: number
  currentRewardPoints: number
  maxDiscountAmount: number
  maxAppliedRewardPoints: number
  earnedRewardPointAmount: number
  hasActivity: boolean
  currentActivityRate: number
  pointUnitStep: number
  currencyUnitStep: number
  activityStartTime?: string
  activityEndTime?: string
}

export const getRewardPointsCalculateResult = (setting: RewardPointsCalculateResultType): RewardPointsCalculateResultReturnType => {
  const incomeRate = setting.income_rate
  const activityRate = setting.activity_rate
  const expenseRate = setting.expense_rate
  const pointDiscountLimitRate = setting.point_discount_limit_rate
  const subtotal = setting.subtotal
  const totalAfterDiscount = setting.total_after_discount
  const currentRewardPoints = setting.current_reward_points
  const countryCode = setting.country_code
  const pointUnitStep = setting.point_unit_step
  const currencyUnitStep = setting.currency_unit_step
  const activityStartTime = setting.activity_start_time
  const activityEndTime = setting.activity_end_time
  const totalCouponDiscount = setting.total_coupon_discount

  /**
   * 整單最高可折抵金額
   * 美國站會給到小數點後一位
   */
  const maxDiscountAmount = getMaxRewardPointsDiscountAmount(
    currentRewardPoints,
    expenseRate,
    subtotal,
    pointDiscountLimitRate,
    countryCode,
    totalCouponDiscount,
  )
  // 整單最高可折抵點數
  const maxAppliedRewardPoints = maxDiscountAmount * expenseRate

  const hasActivity = setting.is_activity && isActivityTime(activityStartTime, activityEndTime)
  // 本次消費可獲得點數
  const earnedRewardPointAmount = getEarnedRewardPointAmount(
    hasActivity,
    incomeRate,
    activityRate,
    totalAfterDiscount,
  )

  const DEFAULT_RATE = 1

  const currentActivityRate = hasActivity ? activityRate : DEFAULT_RATE

  return {
    incomeRate,
    activityRate,
    expenseRate,
    pointDiscountLimitRate,
    totalAfterDiscount,
    currentRewardPoints,
    maxDiscountAmount,
    maxAppliedRewardPoints,
    earnedRewardPointAmount,
    hasActivity,
    currentActivityRate,
    pointUnitStep,
    currencyUnitStep,
    activityStartTime,
    activityEndTime,
  }
}
